import type { AuthClient, Claims, Session } from "./types";

/**
 * Same interface, no network: for local development before a Cognito pool
 * exists and for tests. Sign-in succeeds immediately as the configured user.
 */
export function createMockAuthClient(options: { user?: Claims & { amr?: string }; signedIn?: boolean; navigate?: (url: string) => void } = {}): AuthClient {
  const user: Claims & { amr?: string } = options.user ?? { sub: "mock-user", email: "learner@example.test", name: "Mock Learner", groups: ["learn"] };
  const navigate = options.navigate ?? ((url: string) => window.location.assign(url));
  const listeners = new Set<(s: Session | null) => void>();
  // An unsigned, clearly labelled token so the local dev API can read the claims. The deployed API never accepts it.
  const token = `mock.${mockEncode(JSON.stringify(user))}.local`;
  const make = (): Session => ({ accessToken: token, idToken: token, expiresAt: Date.now() + 3_600_000, claims: { sub: user.sub, email: user.email, name: user.name, groups: user.groups } });
  let session: Session | null = options.signedIn === false ? null : make();
  let returnTo = "/";
  const commit = (s: Session | null) => {
    session = s;
    for (const l of listeners) l(s);
  };
  return {
    async signIn(o) {
      returnTo = o?.returnTo ?? "/";
      navigate(`/auth/callback?code=mock&state=mock`);
    },
    async handleCallback() {
      commit(make());
      return { returnTo };
    },
    async getSession() {
      return session;
    },
    async signOut() {
      session = null; // no broadcast, as in the real client: RequireAuth must not start a sign-in over the navigation
      navigate("/auth/signed-out");
    },
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

function mockEncode(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
